import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DetailPanelClickRect } from 'src/components/dispatch/detailPanel/DetailPanelShell';
import { DispatchSelection, GeoUpdatePayload } from 'src/components/dispatch/detailPanel/DispatchDetailPanel';
import { resolveMoveSourceRowId } from 'src/components/dispatch/moveSourceRow';
import { GetBusyGuardReason } from 'src/components/dispatch/resolveBusyGuardReason';
import { UnassignedOptimistic } from 'src/components/dispatch/unassignedOptimistic';
import { DispatchCard } from 'src/entities/dispatch';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { useBulkUpdateWaypointLocationMutation } from 'src/hooks/useBulkUpdateWaypointLocation.mutation';
import {
  CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY,
  CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY,
} from 'src/hooks/useConvenienceDispatchDeliveries.query';
import { useKeepSetIdentity } from 'src/hooks/useKeepIdentity';
import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { GeoAlertInfo } from 'src/utils/geoAlerts';

import { VehicleRowGroup } from './adapter';
import { resolveSelectionFromLatest, selectionKey } from './detailSelectionSync';
import {
  buildLegRunMaterialsMap,
  buildRowIdToShiftTruckId,
  buildRowLookup,
  resolveShipmentOrderIdsFromUnassignedIds,
} from './rowLookup';

export type ConfirmDialogState = {
  title: string;
  description: string;
  onConfirm: () => void | Promise<void>;
};

/** 「他車両へ配車」で自動配車ダイアログへ引き継ぐ移動情報 */
export type MoveTarget = {
  /** 積地カードからも移せる (logposeTech/log_web_issues#320) ので DispatchCard で受ける */
  sourceCard: DispatchCard;
  sourceRowId: string;
  targetRowId: string;
  targetDeliveryId: number;
  targetShiftId: number;
  sourceShiftId: number;
  targetVehicleLabel: string;
  orderIds: number[];
};

/**
 * 「未割り当てに戻す」確認ダイアログの本文。
 *
 * Why 積地だけ件数を出す: 積地カードは「その回転でその積地から積む案件」を 1 枚に束ねる
 * （同じ積地への再訪もマージされる）ので、1 操作で複数案件が動く。何件動くのかを
 * 押す前に見せる (logposeTech/log_web_issues#320)。降地は従来の文言を保つ。
 */
function resetConfirmDescription(card: DispatchCard, targetCount: number): string {
  if (card.type === 'loading') {
    // 地点名は API 由来で欠けうる。詳細パネルの表題 (DispatchDetailPanel) と同じ呼び方に揃える
    const placeName = card.locationName ?? '積み込み拠点';
    return `「${placeName}」から積む ${targetCount} 件の案件を外して未割り当てに戻します。よろしいですか？`;
  }
  return `「${card.storeName}」を外して未割り当てに戻します。よろしいですか？`;
}

export type UseDispatchDetailActionsParams = {
  deliveries: PlanningsDeliveryWithLegRunEntity[];
  rowGroups: VehicleRowGroup[];
  legRuns: ShipmentOrderLegRunForPlanEntity[];
  /** GA の screen_name。器（DetailPanelShell）と周辺ダイアログへそのまま渡す */
  screenName: string;
  /** `${companyId}:${startOn}:${endOn}`。変わったらこの hook の state を破棄する */
  scopeKey: string;
  applyResetToCaches: (ids: number[]) => void;
  operationBulkResetting: { mutateAsync: (ids: number[]) => Promise<unknown> };
  deleteShipmentOrders: { mutateAsync: (ids: number[]) => Promise<unknown> };
  /** 案件コピー。同じ内容の shipment_order をもう 1 件作る */
  cloneShipmentOrder: { mutateAsync: (id: number) => Promise<unknown> };
  /** 分割成功後に未割当リストを更新する */
  refetchLegRuns: () => void;
  setConfirmDialog: (v: ConfirmDialogState | null) => void;
  notify: (message: string, variant?: 'success' | 'error' | 'info') => void;
  unassignedOptimistic: UnassignedOptimistic;
  /** 詳細を閉じるとき画面側も片付ける（カード配車は selectedCardIds を解除する） */
  onCloseDetail?: () => void;
  /** scope が変わったときの画面ローカルの破棄 */
  onScopeChanged: () => void;
  /** 未指定なら「他車両へ配車」を出さない */
  openTransferPlanning?: (target: MoveTarget) => void;
  resetTransferPlanning?: () => void;
  /**
   * 自動配車の実行中に競合する書き込みを弾く（Task 10）。未指定ならガードしない
   * （カード配車は現状ガードを渡さない。必須にすると同時に変える必要が出るため）。
   * onConfirm 実行時点の最新状態を読ませるため、値ではなく getter で受ける。
   */
  getBusyGuardReason?: GetBusyGuardReason;
};

export type DispatchDetailActions = {
  screenName: string;
  detailSelection: DispatchSelection;
  detailClickRect: DetailPanelClickRect | null;
  detailOpen: boolean;
  openDetail: (selection: DispatchSelection, clickRect: DetailPanelClickRect | null) => void;
  closeDetail: () => void;
  moveMenuAnchor: HTMLElement | null;
  setMoveMenuAnchor: (el: HTMLElement | null) => void;
  duplicateStoreNames: Set<string>;
  geoAlerts: Map<string, GeoAlertInfo>;
  /** 未割当 id (= `lr-{legRunId}`) → shipment_order_id。器の「案件編集」の有効/無効に使う */
  resolveShipmentOrderId: (itemId: string) => number | null;
  handleGeoUpdate: (payload: GeoUpdatePayload) => void;
  handleEditLane: (laneId: number) => void;
  handleEditUnassignedOrder: (shipmentOrderId: number) => void;
  /** openTransferPlanning が未注入なら undefined（器がボタンを描かない） */
  handleMoveToVehicle: ((targetRowId: string) => void) | undefined;
  handleResetToUnassigned: () => void;
  handleOpenSplitDialog: () => void;
  handleSplitSuccess: () => void;
  handleDeleteUnassignedFromDetail: () => void;
  handleCopyUnassignedFromDetail: () => void;
  laneSheetId: number | null;
  closeLaneSheet: () => void;
  editShipmentOrderId: number | null;
  closeEditShipmentOrder: () => void;
  splitDialogOpen: boolean;
  splitTargetLegRun: ShipmentOrderLegRunForPlanEntity | null;
  closeSplitDialog: () => void;
};

/**
 * 詳細パネル（器 = DetailPanelShell）のアクション層。
 * カード配車・地図配車で共有するため、画面固有の機構（未割当の楽観更新・移動先ダイアログ・
 * 選択カードの解除）は注入契約にしている。
 */
export function useDispatchDetailActions(
  params: UseDispatchDetailActionsParams,
): DispatchDetailActions {
  const {
    deliveries,
    rowGroups,
    legRuns,
    screenName,
    scopeKey,
    applyResetToCaches,
    operationBulkResetting,
    deleteShipmentOrders,
    cloneShipmentOrder,
    refetchLegRuns,
    setConfirmDialog,
    notify,
    unassignedOptimistic,
    onCloseDetail,
    onScopeChanged,
    openTransferPlanning,
    resetTransferPlanning,
    getBusyGuardReason,
  } = params;

  const queryClient = useQueryClient();

  // ── 詳細パネル本体の state ────────────────────────────────
  // state に持つのはクリック時のスナップショット。表示・操作に使うのは、そこから
  // 最新の rowGroups へ解決し直した detailSelection（座標更新などの再取得に追随させる）
  const [selectedSnapshot, setSelectedSnapshot] = useState<DispatchSelection>(null);
  const [detailClickRect, setDetailClickRect] = useState<DetailPanelClickRect | null>(null);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<HTMLElement | null>(null);
  const detailSelection = useMemo(
    () => resolveSelectionFromLatest(selectedSnapshot, rowGroups),
    [selectedSnapshot, rowGroups],
  );
  const detailOpen = detailSelection !== null;

  // 応答待ちの完了処理を「開始したときの状況」に閉じ込めるための ref。
  // - generation: scope が変わるたびに増える世代番号。scopeKey 文字列だけだと
  //   A→B→A と戻ったとき別物なのに一致してしまう
  // - detailKey: 開いている詳細の同一性。同じ scope のまま別案件へ切り替えた場合を弾く
  const scopeGenerationRef = useRef(0);
  const detailKeyRef = useRef<string | null>(null);
  useLayoutEffect(() => { detailKeyRef.current = selectionKey(detailSelection); }, [detailSelection]);

  const openDetail = useCallback((selection: DispatchSelection, clickRect: DetailPanelClickRect | null) => {
    setDetailClickRect(clickRect);
    setSelectedSnapshot(selection);
  }, []);

  const closeDetail = useCallback(() => {
    setSelectedSnapshot(null);
    setDetailClickRect(null);
    setMoveMenuAnchor(null);
    onCloseDetail?.();
  }, [onCloseDetail]);

  // ── 周辺ダイアログの state ────────────────────────────────
  const [laneSheetId, setLaneSheetId] = useState<number | null>(null);
  const [editShipmentOrderId, setEditShipmentOrderId] = useState<number | null>(null);
  const [splitTargetLegRun, setSplitTargetLegRun] = useState<ShipmentOrderLegRunForPlanEntity | null>(null);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);

  const closeLaneSheet = useCallback(() => { setLaneSheetId(null); }, []);
  const closeEditShipmentOrder = useCallback(() => { setEditShipmentOrderId(null); }, []);
  const closeSplitDialog = useCallback(() => {
    setSplitDialogOpen(false);
    setSplitTargetLegRun(null);
  }, []);

  // ── 派生値 ────────────────────────────────────────────────
  const legRunMaterialsMap = useMemo(() => buildLegRunMaterialsMap(legRuns), [legRuns]);
  const rowLookup = useMemo(() => buildRowLookup(rowGroups), [rowGroups]);
  const rowIdToShiftTruckId = useMemo(() => buildRowIdToShiftTruckId(deliveries), [deliveries]);

  // 重複店舗名・固定ルート外（API 連動は今後）
  // 中央ボードの全行が受け取る props なので、参照を据え置く。rowGroups の参照が
  // 変わるだけで新しい Set を流すと、1 台に回転を足しただけで全行の memo が破れる
  const keepDuplicateStoreNames = useKeepSetIdentity<string>();
  const duplicateStoreNames = useMemo<Set<string>>(() => {
    const m = new Map<string, Set<string>>();
    rowGroups.forEach((g) => g.vehicle.cards.forEach((c) => {
      if (c.type !== 'delivery' || c.status === 'no-delivery' || c.status === 'fr-template') return;
      const bucket = m.get(c.storeName);
      if (bucket) bucket.add(g.vehicle.id);
      else m.set(c.storeName, new Set([g.vehicle.id]));
    }));
    return keepDuplicateStoreNames(
      new Set(Array.from(m.entries()).filter(([, v]) => v.size > 1).map(([k]) => k)),
    );
  }, [rowGroups, keepDuplicateStoreNames]);

  // 位置情報関連の警告は現状表示しない方針のため空マップを渡す
  const geoAlerts = useMemo(() => new Map<string, GeoAlertInfo>(), []);

  const resolveShipmentOrderIds = useCallback(
    (ids: string[]) => resolveShipmentOrderIdsFromUnassignedIds(ids, legRuns),
    [legRuns],
  );
  const resolveShipmentOrderId = useCallback(
    (itemId: string) => resolveShipmentOrderIds([itemId])[0] ?? null,
    [resolveShipmentOrderIds],
  );

  // ── 座標を編集 ────────────────────────────────────────────
  // 対象カードの全 legRun の waypoint と、同 (name, address) の Place を一括で更新する。
  const bulkUpdateWaypointLocation = useBulkUpdateWaypointLocationMutation();
  const handleGeoUpdate = useCallback(
    (payload: GeoUpdatePayload) => {
      if (payload.legRunIds.length === 0) return;
      // Task 10 #6: 確認を経ない操作なので、呼び出し時点の busy をここで直接読む。
      const busyReason = getBusyGuardReason?.();
      if (busyReason) {
        notify(busyReason, 'error');
        return;
      }
      bulkUpdateWaypointLocation.mutate(
        {
          legRunIds: payload.legRunIds,
          side: payload.side,
          lat: payload.newGeo.lat,
          lng: payload.newGeo.lng,
        },
        {
          onSuccess: () => {
            notify('座標を更新しました', 'success');
            void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
            void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
            void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
          },
          onError: (e: Error) => {
            const err = e as AxiosError<{ error?: string }>;
            notify(err.response?.data?.error ?? '座標の更新に失敗しました', 'error');
          },
        },
      );
    },
    [bulkUpdateWaypointLocation, notify, queryClient, getBusyGuardReason],
  );

  // ── レーン管理 / 案件編集 ─────────────────────────────────
  const handleEditLane = useCallback((laneId: number) => {
    // カード詳細ポップアップは閉じてからレーン詳細ドロワーを開く
    closeDetail();
    setLaneSheetId(laneId);
  }, [closeDetail]);

  const handleEditUnassignedOrder = useCallback((shipmentOrderId: number) => {
    // カード詳細ポップアップを閉じてから案件編集ダイアログを開く
    closeDetail();
    setEditShipmentOrderId(shipmentOrderId);
  }, [closeDetail]);

  // ── 伝票を分割 ────────────────────────────────────────────
  const handleOpenSplitDialog = useCallback(() => {
    if (detailSelection?.source !== 'unassigned') return;
    const rawId = detailSelection.item.id;
    const legRunId = rawId.startsWith('lr-') ? parseInt(rawId.slice(3), 10) : NaN;
    if (Number.isNaN(legRunId)) return;
    const entity = legRunMaterialsMap.get(legRunId);
    if (!entity) return;
    setSplitTargetLegRun(entity);
    setSplitDialogOpen(true);
  }, [detailSelection, legRunMaterialsMap]);

  const handleSplitSuccess = useCallback(() => {
    refetchLegRuns();
  }, [refetchLegRuns]);

  // ── 他車両へ配車 ──────────────────────────────────────────
  // V3Plans と同じく、移動先車両選択の直後に「自動配車の設定」ダイアログを開く。
  const moveToVehicle = useCallback((targetRowId: string) => {
    setMoveMenuAnchor(null);
    // 積地カードからも移せる (logposeTech/log_web_issues#320)。積地・降地とも
    // 動かす単位は card.legRunIds なので、以降の手順は type で分かれない
    if (detailSelection?.source !== 'vehicle') return;
    const sourceCard = detailSelection.card;
    const sourceRowId = resolveMoveSourceRowId(detailSelection, rowGroups);
    const targetRow = rowLookup.get(targetRowId);
    if (sourceRowId == null || !targetRow) return;
    if (sourceRowId === targetRow.rowId) return;

    const targetGroup = rowGroups.find((g) => g.rows.some((r) => r.rowId === targetRow.rowId));
    const targetVehicleLabel = targetGroup?.vehicle.vehicleNumber ?? '選択した車両';

    const targetDelivery = deliveries.find((d) => d.id === (rowIdToShiftTruckId.get(targetRow.rowId) ?? -1));
    const sourceDelivery = deliveries.find((d) => d.id === (rowIdToShiftTruckId.get(sourceRowId) ?? -1));
    if (!targetDelivery || !sourceDelivery) {
      notify('移動先または移動元の delivery が特定できませんでした', 'error');
      return;
    }

    const legRunIds = (sourceCard.legRunIds ?? []).filter((n): n is number => typeof n === 'number');
    if (legRunIds.length === 0) {
      notify('移動対象の案件データが見つかりません', 'error');
      return;
    }

    openTransferPlanning?.({
      sourceCard,
      sourceRowId,
      targetRowId: targetRow.rowId,
      targetDeliveryId: targetDelivery.id,
      targetShiftId: targetDelivery.shiftId,
      sourceShiftId: sourceDelivery.shiftId,
      targetVehicleLabel,
      orderIds: legRunIds,
    });
    // 詳細ポップは閉じて「自動配車の設定」ダイアログだけを前面に出す
    closeDetail();
  }, [detailSelection, rowGroups, rowLookup, deliveries, rowIdToShiftTruckId, notify, closeDetail, openTransferPlanning]);

  const handleMoveToVehicle = openTransferPlanning ? moveToVehicle : undefined;

  // 今の scope。scope 変更の検知（下の useLayoutEffect）と、応答待ちの完了処理の
  // scope 照合の両方に使う。render 中の scopeKey だと、応答が返るフレームには既に
  // 新しい会社・日付になっていることがある
  const appliedScopeKeyRef = useRef(scopeKey);

  // ── 案件を削除 ────────────────────────────────────────────
  /**
   * 案件のコピーを作る（旧地図配車 V3Plans presenters/OrderPresenter.tsx:118-125）。
   * 削除と違って画面から消えるものが無いので楽観的更新はせず、成功後に取り直す。
   */
  const handleCopyUnassignedFromDetail = useCallback(() => {
    if (detailSelection?.source !== 'unassigned') return;
    const shipmentOrderIds = resolveShipmentOrderIds([detailSelection.item.id]);
    if (shipmentOrderIds.length === 0) {
      notify('コピー対象の案件が見つかりません', 'error');
      return;
    }
    const storeName = detailSelection.item.storeName;

    setConfirmDialog({
      title: '案件をコピーしますか？',
      description: `「${storeName}」と同じ内容の案件をもう 1 件作ります。`,
      onConfirm: async () => {
        // 自動配車の実行中に案件が増えると、計算結果と画面が食い違う（削除と同じガード）
        const busyReason = getBusyGuardReason?.();
        if (busyReason) { notify(busyReason, 'error'); return; }

        try {
          await cloneShipmentOrder.mutateAsync(shipmentOrderIds[0]);
        } catch (e) {
          const err = e as AxiosError<{ message: string }>;
          notify(err.response?.data?.message ?? 'コピーに失敗しました', 'error');
          return;
        }
        notify('案件をコピーしました', 'success');
        // 増えた案件は取り直さないと画面に出ない
        refetchLegRuns();
      },
    });
  }, [detailSelection, resolveShipmentOrderIds, cloneShipmentOrder, getBusyGuardReason,
    refetchLegRuns, setConfirmDialog, notify]);

  const handleDeleteUnassignedFromDetail = useCallback(() => {
    if (detailSelection?.source !== 'unassigned') return;
    const targetId = detailSelection.item.id;
    const storeName = detailSelection.item.storeName;
    const shipmentOrderIds = resolveShipmentOrderIds([targetId]);
    if (shipmentOrderIds.length === 0) {
      notify('削除対象の案件が見つかりません', 'error');
      return;
    }
    // 削除対象 shipment_order 配下の legRun id 全てを未割当から取り除く必要があるため
    // (1 shipment_order が複数 legRun を持つ場合に備える)
    const shipmentOrderIdSet = new Set(shipmentOrderIds);
    const removedItemIds = new Set<string>();
    legRuns.forEach((lr) => {
      if (lr.id != null && shipmentOrderIdSet.has(lr.shipment_order_id)) {
        removedItemIds.add(`lr-${lr.id}`);
      }
    });
    setConfirmDialog({
      title: '案件を削除しますか？',
      description: `「${storeName}」を削除します。元には戻せません。`,
      onConfirm: async () => {
        // Task 10 #5: 楽観的更新 (unassignedOptimistic.remove) 自体も書き込みの一種なので、
        // ガードは楽観的更新より前に置く（後ろに置くと画面からだけ消えて実データは残る）。
        const busyReason = getBusyGuardReason?.();
        if (busyReason) {
          notify(busyReason, 'error');
          return;
        }
        // 楽観的更新: 先に画面から消し、ポップアップを閉じる
        const removal = unassignedOptimistic.remove(removedItemIds);
        closeDetail();

        try {
          await deleteShipmentOrders.mutateAsync(shipmentOrderIds);
          notify('案件を削除しました', 'success');
          // 成功時も操作を終端する。放置すると楽観状態が残り、以後サーバ側で
          // 増えた未割当案件が画面に出なくなる
          removal.commit();
        } catch (e) {
          const err = e as AxiosError<{ message: string }>;
          notify(err.response?.data?.message ?? '削除に失敗しました', 'error');
          // 失敗時はこの操作で消した分だけ戻す
          removal.rollback();
        }
      },
    });
  }, [
    detailSelection, resolveShipmentOrderIds, deleteShipmentOrders,
    notify, closeDetail, legRuns, setConfirmDialog, unassignedOptimistic, getBusyGuardReason,
  ]);

  // ── 未割り当てに戻す ──────────────────────────────────────
  const handleResetToUnassigned = useCallback(() => {
    // 積地カードからも戻せる (logposeTech/log_web_issues#320)
    if (detailSelection?.source !== 'vehicle') return;
    const sourceCard = detailSelection.card;
    const legRunIds = (sourceCard.legRunIds ?? []).filter((n): n is number => typeof n === 'number');
    if (legRunIds.length === 0) {
      notify('未割当に戻せる案件データが見つかりません', 'error');
      return;
    }
    setConfirmDialog({
      title: '未割り当てに戻しますか？',
      description: resetConfirmDescription(sourceCard, legRunIds.length),
      onConfirm: async () => {
        // Task 10 #4: ダイアログを開いた後に busy へ変わった場合の保険。
        const busyReason = getBusyGuardReason?.();
        if (busyReason) {
          notify(busyReason, 'error');
          return;
        }
        const startedAt = { generation: scopeGenerationRef.current, detailKey: detailKeyRef.current };
        let apiSucceeded = false;
        try {
          await operationBulkResetting.mutateAsync(legRunIds);
          apiSucceeded = true;
        } catch (e) {
          const err = e as AxiosError<{ message: string }>;
          notify(err.response?.data?.message ?? '処理に失敗しました', 'error');
        }
        if (apiSucceeded) {
          // データはサーバで実際に変わっているので、scope に関係なくキャッシュへ反映する
          applyResetToCaches(legRunIds);
          notify(`${legRunIds.length} 件の案件を未割当に戻しました`, 'success');
          // 応答待ちの間に会社・日付が変わった / 別の案件の詳細に切り替わった場合、
          // いま開いているものはこの操作とは無関係なので閉じない
          const isSameTarget = scopeGenerationRef.current === startedAt.generation
            && detailKeyRef.current === startedAt.detailKey;
          if (isSameTarget) closeDetail();
        }
      },
    });
  }, [
    detailSelection, operationBulkResetting, notify, applyResetToCaches,
    closeDetail, setConfirmDialog, getBusyGuardReason,
  ]);

  // ── scope（会社・期間）が変わったら開いているものを全部閉じる ──
  // 旧会社・旧日付のカードを開いたまま操作させない。初回 mount では走らせない
  // （走ると開いた瞬間に閉じる／不要な破棄通知が飛ぶ）
  useLayoutEffect(() => {
    if (appliedScopeKeyRef.current === scopeKey) return;
    appliedScopeKeyRef.current = scopeKey;
    // A→B→A と戻ったとき scopeKey は元に戻るが、間に開き直したものは別物。
    // 応答待ちの完了処理はこの世代番号で照合する
    scopeGenerationRef.current += 1;
    closeDetail();
    setLaneSheetId(null);
    setEditShipmentOrderId(null);
    closeSplitDialog();
    setConfirmDialog(null);
    resetTransferPlanning?.();
    onScopeChanged();
  }, [scopeKey, closeDetail, closeSplitDialog, setConfirmDialog, resetTransferPlanning, onScopeChanged]);

  return {
    screenName,
    detailSelection,
    detailClickRect,
    detailOpen,
    openDetail,
    closeDetail,
    moveMenuAnchor,
    setMoveMenuAnchor,
    duplicateStoreNames,
    geoAlerts,
    resolveShipmentOrderId,
    handleGeoUpdate,
    handleEditLane,
    handleEditUnassignedOrder,
    handleMoveToVehicle,
    handleResetToUnassigned,
    handleOpenSplitDialog,
    handleSplitSuccess,
    handleDeleteUnassignedFromDetail,
    handleCopyUnassignedFromDetail,
    laneSheetId,
    closeLaneSheet,
    editShipmentOrderId,
    closeEditShipmentOrder,
    splitDialogOpen,
    splitTargetLegRun,
    closeSplitDialog,
  };
}
